import { differenceInDays } from "date-fns";
import { inject } from "mobx-react";
import React, { SyntheticEvent, useCallback, useEffect, useState } from "react";
import type { Store } from "../../../../../../client/stores";
import { AgentOSType } from "../../../../../../client/utils";
import { Remote } from "../../../../../core/components/remote/Remote.component";
import { isPWSSessionBackend, PWSSessionState } from "../../../../../core/lib/auth/backends/pws";
import { coastMediaService } from "../../../../../core/services/PWS";
import { Button, ButtonKind, FauxButton } from "../../../../../core/ui";
import Heading from "../../../../../core/ui/Heading.component";
import { COAST_SHOW_TYPE } from "../../../lib/coastContent";
import { CoastShowAudioBoxMP3DownloadNotice } from "./CoastShowAudioBoxMP3DownloadNotice.component";

interface CoastShowAudioboxMP3Props {
    downloadLinks: Record<string, string | null>;
    osType: AgentOSType | null;
    store?: Store;
}

interface CoastShowAudioboxMP3LoaderProps {
    osType: AgentOSType | null;
    showType: COAST_SHOW_TYPE;
    showDate: Date | number;
    store?: Store;
}

const CoastShowAudioboxMP3 = inject("store")(({ store, osType, downloadLinks }: CoastShowAudioboxMP3Props) => {
    const handleClick = useCallback(
        (e: SyntheticEvent) => {
            e.preventDefault();

            const session = store?.session.currentSession;

            if (!isPWSSessionBackend(session) || session.state.hasActiveSubscription) {
                return null;
            }

            if (session.state.authenticated) {
                session.showMissingSubscriptionPopup();
            } else {
                session.login();
            }
        },
        [store?.session.currentSession],
    );

    if (!store) {
        return null;
    }

    return (
        <>
            <section className="audiobox-section coast-mp3">
                <Heading level={4} className="audiobox-title">
                    Download MP3s
                </Heading>

                <div className="audiobox-section-value">
                    {Object.entries(downloadLinks).map(([hour, url]) =>
                        url ? (
                            <FauxButton
                                kind={ButtonKind.CUSTOMIZED}
                                className="media-link"
                                download="download"
                                key={hour}
                                to={url}
                            >
                                Hour {hour}
                            </FauxButton>
                        ) : (
                            <Button className="media-link" click={handleClick} key={hour} kind={ButtonKind.CUSTOMIZED}>
                                Hour {hour}
                            </Button>
                        ),
                    )}
                </div>
            </section>
            <CoastShowAudioBoxMP3DownloadNotice osType={osType} />
        </>
    );
});

const mp3Loader = (showDate: number | Date, session: PWSSessionState): Promise<Record<string, string | null>> =>
    session.accessToken && session.hasActiveSubscription
        ? coastMediaService.getMP3LinksBulk(showDate, session.accessToken)
        : Promise.resolve({ 1: null, 2: null, 3: null, 4: null });

export const CoastShowAudioboxMP3Loader = inject("store")(
    ({ store, showDate, showType, ...rest }: CoastShowAudioboxMP3LoaderProps) => {
        const session = store?.session.currentSession;
        if (!store || !isPWSSessionBackend(session)) {
            return null;
        }

        const [hasActiveSubscription, setHasActiveSubscription] = useState(session.state.hasActiveSubscription);

        const handleSessionUpdate = useCallback(
            (state: PWSSessionState) => setHasActiveSubscription(state.hasActiveSubscription),
            [],
        );

        useEffect(() => {
            session.onStatusChanged.subscribe(handleSessionUpdate);
            return () => session.onStatusChanged.unsubscribe(handleSessionUpdate);
        }, [handleSessionUpdate, session.onStatusChanged]);

        // hide mp3 downloads on regular shows that are more than 7 years old when comparing the difference in days
        if (showType === COAST_SHOW_TYPE.REGULAR && differenceInDays(new Date(), showDate) > 7 * 365) {
            return null;
        }

        return (
            <Remote
                loader={() => mp3Loader(showDate, session.state)}
                cacheKey={`coast-show-audiobox-mp3-${Number(showDate)}-${hasActiveSubscription}`}
                showLoading={true}
            >
                {({ data }) => (data ? <CoastShowAudioboxMP3 downloadLinks={data} {...rest} /> : null)}
            </Remote>
        );
    },
);
